/**
 * User Management — admin-only Type-3 surface at /admin/users (Day 99b.2).
 *
 * Graduates Settings → Users (the Day-63a FormulaEditor graduation precedent) +
 * adds: an "Invite user" affordance (the invite-user EF), in-app reactivate
 * (reactivate_user RPC), and a user-scoped activity panel over audit_log
 * (edit_type='security'). Mirrors the Dashboard/SystemHealth Type-3 shell.
 *
 * Role / distributor / name edits are direct profiles.update (the existing
 * UsersSection controls) — they now pass through the Day-99b.2
 * guard_profile_privileged_update (admin actor passes; a last-admin demotion is
 * blocked → 42501 → surfaced as a TONE-compliant toast) and the role-change
 * audit trigger. deactivate/reactivate go through the SD RPCs (which also audit).
 * Bounded user count → a plain non-virtualized table, NOT AG Grid IRM (B1).
 *
 * No nav badge (B6 — confirmation/pending-invite state is auth.users-only, not
 * PostgREST-readable; deferred, consistent with the Algopix-badge deferral).
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { UserPlus } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { FormModal } from '../components/ui/FormModal';
import ArbiSelect from '../components/ui/ArbiSelect';

// ── Local types ───────────────────────────────────────────────────────────────

type UserRole = 'admin' | 'user' | 'viewer' | 'dist_rep';

const ROLE_OPTIONS: ReadonlyArray<{ value: UserRole; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User (VA)' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'dist_rep', label: 'Dist. Rep' },
];

interface UserProfile {
  id: string;
  role: UserRole;
  distributor_id: string | null;
  full_name: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface AuthMeta {
  email: string | null | undefined;
  last_sign_in_at: string | null | undefined;
}
type AuthMetaState = 'loading' | 'ok' | 'error';

interface SecurityEvent {
  id: string;
  action: string;
  entity_id: string | null;
  actor_id: string | null;
  detail: unknown;
  created_at: string;
}

interface DistOption {
  value: string;
  label: string;
}

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

const ACTIVITY_PAGE = 20;

// ── Helpers (inline-replicated per arbi-dev precedent) ──────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatLastSignIn(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const ageDays = (Date.now() - t) / 86400000;
  if (ageDays < 7) {
    if (ageDays < 1 / 24) return 'just now';
    if (ageDays < 1) return `${Math.floor(ageDays * 24)}h ago`;
    return `${Math.floor(ageDays)}d ago`;
  }
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function joinedDateOf(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Toast ───────────────────────────────────────────────────────────────────--

function Toast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

// ── User row (graduated from Settings UsersSection + reactivate) ────────────────

interface UserRowProps {
  profile: UserProfile;
  distOptions: DistOption[];
  authMeta: AuthMeta | undefined;
  authMetaState: AuthMetaState;
  isSelf: boolean;
  onRoleChange: (id: string, role: UserRole) => void;
  onDistributorChange: (id: string, distId: string | null) => void;
  onNameChange: (id: string, name: string) => void;
  onDeactivateClick: () => void;
  onReactivateClick: () => void;
}

function UserRow({
  profile,
  distOptions,
  authMeta,
  authMetaState,
  isSelf,
  onRoleChange,
  onDistributorChange,
  onNameChange,
  onDeactivateClick,
  onReactivateClick,
}: UserRowProps): JSX.Element {
  const [editingName, setEditingName] = useState<boolean>(false);
  const [nameVal, setNameVal] = useState<string>(profile.full_name || '');
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingName && nameRef.current) nameRef.current.focus();
  }, [editingName]);

  function saveName(): void {
    onNameChange(profile.id, nameVal.trim());
    setEditingName(false);
  }
  function handleNameKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveName();
    }
    if (e.key === 'Escape') setEditingName(false);
  }

  const isDeactivated = profile.is_active === false;

  return (
    <div className={clsx('flex items-center gap-3 py-3', isDeactivated && 'opacity-50')}>
      {/* Name */}
      <div className="w-32">
        {editingName ? (
          <input
            ref={nameRef}
            value={nameVal}
            onChange={(e) => setNameVal(e.target.value)}
            onKeyDown={handleNameKeyDown}
            onBlur={saveName}
            className="arbi-input w-full text-xs"
            placeholder="Full name"
          />
        ) : (
          <button
            onClick={() => {
              setNameVal(profile.full_name || '');
              setEditingName(true);
            }}
            className="text-sm text-ink-primary hover:text-accent transition-colors cursor-pointer truncate block w-full text-left"
            title="Click to edit name"
          >
            {profile.full_name || <span className="text-ink-muted italic">No name</span>}
          </button>
        )}
      </div>

      {/* Role */}
      <div className="w-28">
        <ArbiSelect
          value={profile.role}
          options={[...ROLE_OPTIONS]}
          onChange={(val) => onRoleChange(profile.id, String(val) as UserRole)}
          className="w-full"
        />
      </div>

      {/* Distributor — only meaningful for dist_rep */}
      <div className="w-32">
        {profile.role === 'dist_rep' ? (
          <ArbiSelect
            value={profile.distributor_id || ''}
            options={[{ value: '', label: 'None' }, ...distOptions]}
            onChange={(val) => onDistributorChange(profile.id, String(val) || null)}
            className="w-full"
          />
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        )}
      </div>

      {/* Email */}
      <div className="flex-1 min-w-0 text-xs text-ink-secondary">
        {authMetaState === 'loading' ? (
          <span className="text-ink-muted">…</span>
        ) : authMeta?.email ? (
          <span className="font-mono truncate block" title={authMeta.email}>
            {authMeta.email}
          </span>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </div>

      {/* Last sign-in */}
      <div className="w-24 text-xs text-ink-muted font-mono">
        {authMetaState === 'loading' ? '…' : formatLastSignIn(authMeta?.last_sign_in_at)}
      </div>

      {/* Joined */}
      <div className="w-20 text-xs text-ink-muted font-mono">{joinedDateOf(profile.created_at)}</div>

      {/* Deactivate / Reactivate */}
      <div className="w-24 flex justify-end">
        {isDeactivated ? (
          <button onClick={onReactivateClick} className="arbi-btn-ghost text-2xs px-2 py-0.5">
            Reactivate
          </button>
        ) : (
          <button
            onClick={onDeactivateClick}
            disabled={isSelf}
            title={isSelf ? 'Cannot deactivate self' : 'Deactivate user'}
            className={clsx('arbi-btn-ghost text-2xs px-2 py-0.5', isSelf && 'opacity-40 cursor-not-allowed')}
          >
            Deactivate
          </button>
        )}
      </div>
    </div>
  );
}

// ── Activity row (audit_log security slice) ─────────────────────────────────────

const ACTION_LABEL: Record<string, { label: string; cls: string }> = {
  role_change: { label: 'Role change', cls: 'bg-warning/15 text-warning' },
  invite: { label: 'Invite', cls: 'bg-accent/15 text-accent' },
  deactivate: { label: 'Deactivate', cls: 'bg-danger/15 text-danger' },
  reactivate: { label: 'Reactivate', cls: 'bg-success/15 text-success' },
};

function ActivityRow({
  ev,
  nameOf,
}: {
  ev: SecurityEvent;
  nameOf: (id: string | null) => string;
}): JSX.Element {
  const meta = ACTION_LABEL[ev.action] ?? { label: ev.action, cls: 'bg-surface-3 text-ink-muted' };
  const d = ev.detail && typeof ev.detail === 'object' ? (ev.detail as Record<string, unknown>) : {};
  let detailText = '';
  if (ev.action === 'role_change') detailText = `${String(d.from ?? '?')} → ${String(d.to ?? '?')}`;
  else if (ev.action === 'invite') detailText = `as ${String(d.role ?? 'user')}`;

  return (
    <div className="flex items-center gap-2 py-2 text-xs">
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', meta.cls)}>
        {meta.label}
      </span>
      <span className="text-ink-primary truncate">{nameOf(ev.entity_id)}</span>
      {detailText && <span className="text-ink-secondary font-mono">{detailText}</span>}
      <span className="text-ink-muted">· by {nameOf(ev.actor_id)}</span>
      <span className="ml-auto text-ink-muted font-mono whitespace-nowrap">{relativeTime(ev.created_at)}</span>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────--

export default function UserManagement(): JSX.Element {
  useDocumentTitle('User Management');

  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [authMeta, setAuthMeta] = useState<Record<string, AuthMeta>>({});
  const [authMetaState, setAuthMetaState] = useState<AuthMetaState>('loading');
  const [distOptions, setDistOptions] = useState<DistOption[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteRole, setInviteRole] = useState<UserRole>('user');
  const [inviting, setInviting] = useState<boolean>(false);

  // Deactivate/reactivate confirm
  const [confirm, setConfirm] = useState<{ kind: 'deactivate' | 'reactivate'; id: string; label: string } | null>(null);

  // Activity panel
  const [activity, setActivity] = useState<SecurityEvent[]>([]);
  const [activityOffset, setActivityOffset] = useState<number>(0);
  const [activityHasMore, setActivityHasMore] = useState<boolean>(false);
  const [activityLoading, setActivityLoading] = useState<boolean>(false);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);

  const loadProfiles = useCallback(async (): Promise<void> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, distributor_id, full_name, is_active, created_at, updated_at')
      .order('created_at', { ascending: true });
    if (error) {
      showToast({ type: 'error', message: `Failed to load users: ${error.message}` });
      setLoading(false);
      return;
    }
    setProfiles((data ?? []) as UserProfile[]);
    setLoading(false);
  }, [showToast]);

  const loadAuthMeta = useCallback(async (): Promise<void> => {
    setAuthMetaState('loading');
    try {
      const { data, error } = await supabase.functions.invoke<{ users: Array<AuthMeta & { id: string }> }>(
        'get-user-auth-meta'
      );
      if (error || !data?.users) {
        setAuthMetaState('error');
        return;
      }
      const map: Record<string, AuthMeta> = {};
      for (const u of data.users) map[u.id] = { email: u.email, last_sign_in_at: u.last_sign_in_at };
      setAuthMeta(map);
      setAuthMetaState('ok');
    } catch {
      setAuthMetaState('error');
    }
  }, []);

  const loadActivity = useCallback(async (offset: number): Promise<void> => {
    setActivityLoading(true);
    const { data, error } = await supabase
      .from('audit_log')
      .select('id, action, entity_id, actor_id, detail, created_at')
      .eq('edit_type', 'security')
      .order('created_at', { ascending: false })
      .range(offset, offset + ACTIVITY_PAGE - 1);
    setActivityLoading(false);
    if (error) {
      showToast({ type: 'error', message: `Failed to load activity: ${error.message}` });
      return;
    }
    const rows = (data ?? []) as SecurityEvent[];
    setActivity((prev) => (offset === 0 ? rows : [...prev, ...rows]));
    setActivityOffset(offset + rows.length);
    setActivityHasMore(rows.length === ACTIVITY_PAGE);
  }, [showToast]);

  useEffect(() => {
    void loadProfiles();
    void loadAuthMeta();
    void loadActivity(0);
    void supabase
      .from('distributors')
      .select('id, name')
      .order('name', { ascending: true })
      .then(({ data }) => {
        if (data) setDistOptions(data.map((d) => ({ value: d.id, label: d.name })));
      });
    void supabase.auth.getUser().then(({ data }) => setSelfId(data.user?.id ?? null));
  }, [loadProfiles, loadAuthMeta, loadActivity]);

  const refreshActivity = useCallback((): void => {
    setActivityOffset(0);
    void loadActivity(0);
  }, [loadActivity]);

  // ── Mutations (direct profiles.update → guard + audit trigger) ─────────────────
  async function handleRoleChange(id: string, role: UserRole): Promise<void> {
    const { error } = await supabase.from('profiles').update({ role }).eq('id', id);
    if (error) {
      showToast({ type: 'error', message: error.message });
      void loadProfiles(); // re-sync the dropdown to the unchanged value
      return;
    }
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, role } : p)));
    showToast({ type: 'success', message: 'Role updated' });
    refreshActivity();
  }

  async function handleDistributorChange(id: string, distId: string | null): Promise<void> {
    const { error } = await supabase.from('profiles').update({ distributor_id: distId }).eq('id', id);
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, distributor_id: distId } : p)));
    showToast({ type: 'success', message: 'Distributor assignment updated' });
  }

  async function handleNameChange(id: string, name: string): Promise<void> {
    const { error } = await supabase.from('profiles').update({ full_name: name || null }).eq('id', id);
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, full_name: name || null } : p)));
  }

  async function handleConfirmAction(): Promise<void> {
    if (!confirm) return;
    const { kind, id } = confirm;
    setConfirm(null);
    const { error } = await supabase.rpc(kind === 'deactivate' ? 'deactivate_user' : 'reactivate_user', {
      p_user_id: id,
    });
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, is_active: kind === 'reactivate' } : p)));
    showToast({ type: 'success', message: `User ${kind === 'deactivate' ? 'deactivated' : 'reactivated'}` });
    refreshActivity();
  }

  async function handleInvite(): Promise<void> {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    const { data, error } = await supabase.functions.invoke<{ error?: string; email?: string }>('invite-user', {
      body: { email, role: inviteRole },
    });
    setInviting(false);
    // The EF returns a 4xx with { error } on duplicate/invalid; functions.invoke
    // surfaces non-2xx as `error`, and the body may also carry { error }.
    const errMsg = error?.message || data?.error;
    if (errMsg) {
      showToast({ type: 'error', message: errMsg });
      return;
    }
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('user');
    showToast({ type: 'success', message: `Invite sent to ${email}` });
    void loadProfiles();
    void loadAuthMeta();
    refreshActivity();
  }

  // ── Name resolution for the activity panel ─────────────────────────────────────
  const nameOf = useCallback(
    (id: string | null): string => {
      if (!id) return 'System';
      const p = profiles.find((x) => x.id === id);
      const email = authMeta[id]?.email;
      return p?.full_name || email || `#${id.slice(-6)}`;
    },
    [profiles, authMeta]
  );

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.kind === 'reactivate' ? 'Reactivate user?' : 'Deactivate user?'}
        message={
          confirm?.kind === 'reactivate'
            ? `${confirm.label} will regain access and be able to log in again.`
            : confirm
              ? `${confirm.label} will be signed out and unable to log in. You can reactivate them later.`
              : ''
        }
        confirmLabel={confirm?.kind === 'reactivate' ? 'Reactivate' : 'Deactivate'}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirm(null)}
      />

      <FormModal
        open={inviteOpen}
        title="Invite user"
        size="md"
        saving={inviting}
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <button onClick={() => setInviteOpen(false)} className="arbi-btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={() => void handleInvite()}
              disabled={inviting || !inviteEmail.trim()}
              className={clsx('arbi-btn-primary text-sm', (inviting || !inviteEmail.trim()) && 'opacity-50 cursor-not-allowed')}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="invite-email" className="text-2xs uppercase tracking-wider text-ink-muted block mb-1">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="name@company.com"
              className="arbi-input w-full text-sm"
            />
          </div>
          <div>
            <span className="text-2xs uppercase tracking-wider text-ink-muted block mb-1">Role</span>
            <ArbiSelect
              value={inviteRole}
              options={[...ROLE_OPTIONS]}
              onChange={(val) => setInviteRole(String(val) as UserRole)}
              className="w-full"
            />
          </div>
          <p className="text-2xs text-ink-muted">
            An invite email is sent; the user sets their own password.
            {inviteRole === 'dist_rep' && ' Assign their distributor in the table once they appear.'}
          </p>
        </div>
      </FormModal>

      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink-bright">User Management</h1>
            <p className="text-xs text-ink-muted">
              {profiles.length === 1 ? '1 user' : `${profiles.length} users`}
            </p>
          </div>
          <button onClick={() => setInviteOpen(true)} className="arbi-btn-primary text-xs">
            <UserPlus size={13} /> Invite user
          </button>
        </div>

        {/* Users table */}
        <div className="arbi-card p-4">
          {loading ? (
            <div className="py-8 text-center text-sm text-ink-muted">Loading users…</div>
          ) : (
            <div className="divide-y divide-border-subtle">
              <div className="flex items-center gap-3 pb-2 text-2xs uppercase tracking-wider text-ink-muted">
                <div className="w-32">Name</div>
                <div className="w-28">Role</div>
                <div className="w-32">Distributor</div>
                <div className="flex-1">Email</div>
                <div className="w-24">Last sign-in</div>
                <div className="w-20">Joined</div>
                <div className="w-24" />
              </div>
              {profiles.map((p) => (
                <UserRow
                  key={p.id}
                  profile={p}
                  distOptions={distOptions}
                  authMeta={authMeta[p.id]}
                  authMetaState={authMetaState}
                  isSelf={selfId === p.id}
                  onRoleChange={(id, role) => void handleRoleChange(id, role)}
                  onDistributorChange={(id, distId) => void handleDistributorChange(id, distId)}
                  onNameChange={(id, name) => void handleNameChange(id, name)}
                  onDeactivateClick={() =>
                    setConfirm({ kind: 'deactivate', id: p.id, label: p.full_name || authMeta[p.id]?.email || 'this user' })
                  }
                  onReactivateClick={() =>
                    setConfirm({ kind: 'reactivate', id: p.id, label: p.full_name || authMeta[p.id]?.email || 'this user' })
                  }
                />
              ))}
            </div>
          )}
          {authMetaState === 'error' && (
            <p className="text-xs text-warning mt-3 pt-3 border-t border-border-subtle">
              Auth metadata unavailable — email + last sign-in will not populate. Check that the
              get-user-auth-meta Edge Function is deployed.
            </p>
          )}
        </div>

        {/* Recent activity (audit_log security slice) */}
        <div className="arbi-card p-4">
          <h3 className="text-xs font-medium text-ink-secondary uppercase tracking-wider mb-2">Recent activity</h3>
          {activity.length === 0 && !activityLoading ? (
            <p className="text-xs text-ink-muted py-2">No security events recorded yet.</p>
          ) : (
            <div className="divide-y divide-border-subtle">
              {activity.map((ev) => (
                <ActivityRow key={ev.id} ev={ev} nameOf={nameOf} />
              ))}
            </div>
          )}
          {activityHasMore && (
            <button
              onClick={() => void loadActivity(activityOffset)}
              disabled={activityLoading}
              className="arbi-btn-ghost text-xs mt-3"
            >
              {activityLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
